import React from "react";
import { Box, Typography } from "@mui/material";
import { Task } from "protogen/tasks_pb";
import { PriorityChip } from "../creation/PrioritySelect";
import { StageChip } from "./workflowStates";
import useIsMobile from "../hooks/useIsMobile";
import { dateStringToDayAndDate } from "./utils";
import { ReactComponent as EllipseIcon } from "../../icons/Menu/Ellipse.svg";
import { Folder } from "lucide-react";
import WithDividers from "components/helpers/WithDividers";
import LinkRouter from "../navigation/LinkRouter";

export interface TaskRowProps {
  task: Task;
  onClick?: (task: Task) => void;
  hideStatus?: boolean;
}

export const ProjectLabel = ({ project }: { project: string }) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <Folder size={16} strokeWidth={1.5} />
      <Typography variant="bodySmall" color="text.secondary">
        {project}
      </Typography>
    </Box>
  );
};

const TaskRow: React.FC<TaskRowProps> = ({
  task,
  onClick,
  hideStatus = false,
}) => {
  const isMobile = useIsMobile();
  const to = `/tasks/${encodeURIComponent(task.ref)}`;

  return (
    <LinkRouter
      to={to}
      onClick={onClick ? () => onClick(task) : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        padding: "16px 24px",
        border: "1px solid #ECECEC",
        borderRadius: "8px",
        cursor: "pointer",
        backgroundColor: "#FFF",
        "&:hover": {
          backgroundColor: "#F9F9F9",
        },
        gap: "16px",
      }}
    >
      {/* Summary/Title Column */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "4px",
          }}
        >
          {task.unreadStatusCount > 0 && (
            <EllipseIcon
              height={8}
              width={8}
              style={{ marginRight: "6px", minWidth: "8px" }}
            />
          )}
          <Typography
            variant="body"
            color="text.primary"
            sx={{
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {task.title}
          </Typography>
        </Box>
        <Typography
          variant="bodySmall"
          color="text.secondary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <WithDividers DividerComponent={() => <span>{" • "}</span>}>
            {[
              task.project ? (
                <ProjectLabel project={task.project} key={0} />
              ) : null,
              <Box key={1}>{task.family?.name}</Box>,
              task.dueDate ? (
                <Box key={2}>{`Due ${
                  dateStringToDayAndDate(task.dueDate, false)[1]
                }`}</Box>
              ) : null,
            ].filter(Boolean)}
          </WithDividers>
        </Typography>
      </Box>

      {/* Status Column */}
      {!hideStatus && !isMobile && (
        <Box
          sx={{
            minWidth: "140px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <StageChip
            stage={task.stage}
            accountType="advisor"
            stageDate={task.stageDate}
          />
        </Box>
      )}

      {/* Priority Column */}
      {!isMobile && (
        <Box sx={{ minWidth: "100px" }}>
          <PriorityChip priority={task.priority} isMobile={isMobile} />
        </Box>
      )}
    </LinkRouter>
  );
};

export default TaskRow;
